import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = { class: "formgrid grid" }
const _hoisted_3 = { class: "field col" }
const _hoisted_4 = { class: "field col" }
const _hoisted_5 = { class: "field" }
const _hoisted_6 = { class: "field" }
const _hoisted_7 = { class: "field" }

import { onBeforeMount, toRefs, ref, computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minLength, email, requiredIf } from '@vuelidate/validators';
import { EMIT, PERMISSIONS } from '@/enum';
import { GeneralProfileInterface } from '@/types';
import { usePeopleStore, useTimezoneStore, useAuthStore, PersonStores, usePeopleTypesStore } from '@/store';
import { can, usePersonTypes } from '@/composables';

interface GeneralPropsInterface {
  data?: GeneralProfileInterface;
  loading?: boolean;
  disableType?: boolean;
}

/**
 * handle props definition and assignment
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'GeneralForm',
  props: {
    data: {},
    loading: { type: Boolean },
    disableType: { type: Boolean }
  },
  setup(__props: any, { emit: __emit }) {

const peopleStore = usePeopleStore();
const timezoneStore = useTimezoneStore();
const profileStore = PersonStores.useUserProfileStore();
const PEOPLE_TYPES = usePeopleTypesStore().PEOPLE_TYPES;
const useAuth = useAuthStore();

const canUpdate = ref({
  general: false,
  type: false,
});

const emit = __emit;

const props = __props;
const { data, loading, disableType } = toRefs(props);

/**
 * The general reference object to contain the form data.
 */
const general = ref({ ...data.value });

const rules = {
  first_name: { required, minLengthValue: minLength(2) },
  last_name: { required, minLengthValue: minLength(2) },
  email: {
    requiredIfCustom: requiredIf(() => {
      return !usePersonTypes.isStudent(profileStore.person?.people_type?.acronym as string);
    }),
    email,
  },
  person_type_id: { required },
  timezone_id: { required },
};

const v$ = useVuelidate(rules, general);

onBeforeMount(async () => {
  if (can([PERMISSIONS.PEOPLE_TYPES_INDEX]) && !peopleStore.peopleTypes.length) await peopleStore.getPeopleTypes();
  if (can([PERMISSIONS.TIMEZONES_INDEX]) && !timezoneStore.timezones.length) await timezoneStore.getTimezones();

  canUpdate.value.general = canUpdateGeneral();
  canUpdate.value.type = canUpdateType();
});

/**
 * Define the request update payload.
 */
const payload = computed(() => {
  const value = general.value;

  return {
    first_name: value.first_name,
    last_name: value.last_name,
    email: value.email,
    timezone_id: value.timezone_id,
    person_type_id: value.person_type_id,
  };
});

/**
 * Check if all component resources have completed loading.
 */
const isLoading = computed(() => {
  return loading.value || peopleStore.loading || timezoneStore.loading;
});

const onSave = () => {
  // Don't allow a submit event unless the fields are valid.
  if (v$.value.$invalid) return;

  emit(EMIT.SAVE, payload.value);
  useAuth.getAuthUserProfile();
  resetDirty();
};

const resetDirty = () => v$.value.$reset();

const clonedGeneralValue = () => {
  return { ...data.value };
};

const onCancel = () => {
  general.value = clonedGeneralValue();
  resetDirty();
};

const fieldStyles = (key: string, styles?: string): string => {
  const validation = v$.value[key].$invalid && v$.value[key].$dirty ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

const canUpdateGeneral = () => {
  const personType: string = profileStore.person?.people_type?.acronym as string;
  let permissionString = 'PEOPLE_UPDATE';
  let str: keyof typeof PERMISSIONS;

  switch (personType) {
    case PEOPLE_TYPES.SUPPORT_ADMIN: {
      permissionString += '_SUPPORT_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SCHOOL_ADMIN: {
      permissionString += '_SCHOOL_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SUPER_ADMIN: {
      permissionString += '_SUPER_ADMIN';
      break;
    }
  }

  str = permissionString as keyof typeof PERMISSIONS;

  return can([PERMISSIONS[str]]);
};

const canUpdateType = () => {
  const authUserType: string = useAuth.acronymType as string;
  const personType: string = profileStore.person?.people_type?.acronym as string;

  if (!canUpdate.value.general) return false;

  if (authUserType === PEOPLE_TYPES.SCHOOL_ADMIN && personType === PEOPLE_TYPES.SCHOOL_ADMIN) return false;

  if (authUserType === PEOPLE_TYPES.SUPPORT_ADMIN && personType === PEOPLE_TYPES.SUPPORT_ADMIN) return false;

  return true;
};

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_FormCard = _resolveComponent("FormCard")!

  return (_openBlock(), _createBlock(_component_FormCard, {
    title: "General",
    dirty: _unref(v$).$anyDirty,
    cancel: onCancel,
    save: onSave,
    "save-disabled": _unref(v$).$anyDirty && _unref(v$).$invalid
  }, {
    form: _withCtx(() => [
      _createElementVNode("form", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("label", {
              for: "firstname",
              class: _normalizeClass({ required: _unref(v$).$anyDirty })
            }, "First Name", 2),
            (isLoading.value)
              ? (_openBlock(), _createBlock(_component_Skeleton, {
                  key: 0,
                  height: "2.3rem"
                }))
              : (_openBlock(), _createBlock(_component_InputText, {
                  key: 1,
                  id: "firstname",
                  modelValue: _unref(v$).first_name.$model,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).first_name.$model) = $event)),
                  class: _normalizeClass(fieldStyles('first_name', 'inputfield w-full')),
                  disabled: !canUpdate.value.general
                }, null, 8, ["modelValue", "class", "disabled"]))
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("label", {
              for: "lastname",
              class: _normalizeClass({ required: _unref(v$).$anyDirty })
            }, "Last Name", 2),
            (isLoading.value)
              ? (_openBlock(), _createBlock(_component_Skeleton, {
                  key: 0,
                  height: "2.3rem"
                }))
              : (_openBlock(), _createBlock(_component_InputText, {
                  key: 1,
                  id: "lastname",
                  modelValue: _unref(v$).last_name.$model,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).last_name.$model) = $event)),
                  class: _normalizeClass(fieldStyles('last_name', 'inputfield w-full')),
                  disabled: !canUpdate.value.general
                }, null, 8, ["modelValue", "class", "disabled"]))
          ])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _cache[5] || (_cache[5] = _createElementVNode("label", { for: "email" }, "Email", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_InputText, {
                key: 1,
                id: "email",
                modelValue: _unref(v$).email.$model,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).email.$model) = $event)),
                type: "email",
                class: _normalizeClass(fieldStyles('email', 'inputfield w-full')),
                disabled: !canUpdate.value.general
              }, null, 8, ["modelValue", "class", "disabled"]))
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("label", {
            for: "type",
            class: _normalizeClass({ required: _unref(v$).$anyDirty })
          }, "Type", 2),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_Dropdown, {
                key: 1,
                id: "type",
                modelValue: _unref(v$).person_type_id.$model,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).person_type_id.$model) = $event)),
                class: _normalizeClass(fieldStyles('person_type_id', 'w-full')),
                options: _unref(peopleStore).peopleTypes,
                "option-value": "id",
                "option-label": "title",
                placeholder: "Select a User Type",
                disabled: !canUpdate.value.type || !!_unref(disableType),
                onChange: _unref(v$).person_type_id.$touch
              }, null, 8, ["modelValue", "class", "options", "disabled", "onChange"]))
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("label", {
            for: "timezone",
            class: _normalizeClass({ required: _unref(v$).$anyDirty })
          }, "Timezone", 2),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_Dropdown, {
                key: 1,
                id: "timezone",
                modelValue: _unref(v$).timezone_id.$model,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$).timezone_id.$model) = $event)),
                class: _normalizeClass(fieldStyles('timezone_id', 'w-full')),
                options: _unref(timezoneStore).timezones,
                "option-value": "id",
                "option-label": "name",
                placeholder: "Select a Timezone",
                disabled: !canUpdate.value.general
              }, null, 8, ["modelValue", "class", "options", "disabled"]))
        ])
      ])
    ]),
    _: 1
  }, 8, ["dirty", "save-disabled"]))
}
}

})